import React, { useEffect, useState } from "react";
import { API_URL } from "../../config";

const CompanySettings = () => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        const response = await fetch(`${API_URL}/api/company-settings`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          signal: controller.signal,
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.error || "Firmeneinstellungen konnten nicht geladen werden.");
        }

        if (controller.signal.aborted) return;

        setForm({
          name: data.name ?? "",
          email: data.email ?? "",
          phone: data.phone ?? "",
          address: data.address ?? "",
          auto_archive_enabled: Number(data.auto_archive_enabled) === 1,
          archive_after_months: String(data.archive_after_months),
        });
      } catch (err) {
        if (!controller.signal.aborted) setError(err.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, []);

  const change = (field, value) => {
    setForm(previous => ({ ...previous, [field]: value }));
    setMessage("");
  };

  const save = async event => {
    event.preventDefault();
    if (saving) return;

    setError("");
    setMessage("");

    const months = Number(form.archive_after_months);

    if (!Number.isInteger(months) || months < 1 || months > 120) {
      setError("Die Archivierungsfrist muss zwischen 1 und 120 Monaten liegen.");
      return;
    }

    setSaving(true);

    try {
      const body = {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
        auto_archive_enabled: form.auto_archive_enabled,
        archive_after_months: months,
      };

      const response = await fetch(`${API_URL}/api/company-settings`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || "Firmeneinstellungen konnten nicht gespeichert werden.");
      }

      setForm({
        ...body,
        email: body.email ?? "",
        phone: body.phone ?? "",
        address: body.address ?? "",
        archive_after_months: String(months),
      });
      setMessage("Firmeneinstellungen gespeichert.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="admin-panel company-settings">
      <h2>Firmeneinstellungen</h2>

      {loading && <p>Einstellungen werden geladen …</p>}
      {error && <p role="alert">{error}</p>}
      {message && <p role="status">{message}</p>}

      {form && (
        <form onSubmit={save}>
          <fieldset disabled={saving}>
            <legend>Unternehmensdaten</legend>

            <label htmlFor="company-name">Unternehmensname *</label>
            <input
              id="company-name"
              value={form.name}
              onChange={event => change("name", event.target.value)}
              maxLength={255}
              required
            />

            <label htmlFor="company-email">Unternehmens-E-Mail</label>
            <input
              id="company-email"
              type="email"
              value={form.email}
              onChange={event => change("email", event.target.value)}
              maxLength={255}
            />

            <label htmlFor="company-phone">Telefon</label>
            <input
              id="company-phone"
              type="tel"
              value={form.phone}
              onChange={event => change("phone", event.target.value)}
              maxLength={50}
            />

            <label htmlFor="company-address">Adresse</label>
            <textarea
              id="company-address"
              value={form.address}
              onChange={event => change("address", event.target.value)}
              maxLength={500}
              rows={3}
            />
          </fieldset>

          <fieldset disabled={saving}>
            <legend>Automatische Archivierung</legend>

            <label>
              <input
                type="checkbox"
                checked={form.auto_archive_enabled}
                onChange={event =>
                  change("auto_archive_enabled", event.target.checked)
                }
              />
              Gebuchte Monatsabschlüsse automatisch archivieren
            </label>

            <label htmlFor="company-archive-months">
              Frist ab Buchungsdatum in Monaten
            </label>
            <input
              id="company-archive-months"
              type="number"
              min={1}
              max={120}
              step={1}
              value={form.archive_after_months}
              onChange={event =>
                change("archive_after_months", event.target.value)
              }
              required
            />

            <p>
              Die Prüfung erfolgt stündlich. Eine kürzere Frist kann bereits
              gebuchte Abschlüsse beim nächsten Durchlauf archivieren.
              Archivierte Einträge bleiben gesperrt.
            </p>
          </fieldset>

          <button type="submit" disabled={saving}>
            {saving ? "Wird gespeichert …" : "Einstellungen speichern"}
          </button>
        </form>
      )}
    </section>
  );
};

export default CompanySettings;